import { type SVGProps } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { TooltipProps } from "recharts";
import {
  Eye,
  FileText,
  Heart,
} from "lucide-react";
import {
  formatOrganicContentKpiTitle,
  formatOrganicPostTypeLabel,
} from "../OrganicMediaDefinitions";
import { formatNumber, toArabicIndicDigitsInString, wrapBidiIsolate } from "src/utils/numberFormat";
import {
  CHART_COLOR_PALETTE,
  CHART_PRIMARY_COLOR,
  CHART_SECONDARY_COLOR,
} from "src/components/charts/ChartPalette";
import type { TrendPoint } from "src/services/OrganicMediaAPIs";

export type MetricKey = "engagementRate" | "organicReach" | "impressions";

export const PERCENT_METRICS: MetricKey[] = ["engagementRate"];
export type TrendGranularity = "daily" | "monthly";

// A "%" (or other literal punctuation) glued onto formatNumber's return value
// sits outside its internal bidi isolate, so it can still be reordered by the
// browser's bidi algorithm in RTL Arabic text. Re-isolate the composed string.
export const isArabicLanguage = (language?: string) => Boolean(language?.toLowerCase().startsWith("ar"));
export const withBidiIsolate = (text: string, language?: string) =>
  isArabicLanguage(language) ? wrapBidiIsolate(text) : text;

export type DateRange = {
  startDate: string;
  endDate: string;
};

export type PercentAxisConfig = {
  domain?: [number, number];
  ticks?: number[];
};

export type PostTableSortKey =
  | "caption"
  | "postType"
  | "impressions"
  | "reach"
  | "likes"
  | "comments"
  | "shares"
  | "saves"
  | "engagementRate";

export const truncateCaption = (text: string, maxLen = 50) =>
  text.length <= maxLen ? text : `${text.slice(0, maxLen)}...`;

export const toTitleCase = (text: string) =>
  text
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .map(word => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word))
    .join(" ");

export const getCaptionPreview = (text: string) => truncateCaption(text, 300);

export const KPI_ICON_MAP: Record<string, typeof FileText> = {
  posts: FileText,
  reach: Eye,
  engagement: Heart,
  follower: FileText,
};

export const ORGANIC_BLUE_PRIMARY = CHART_PRIMARY_COLOR;
export const ORGANIC_BLUE_SECONDARY = CHART_SECONDARY_COLOR;
export const ORGANIC_CHART_COLORS = CHART_COLOR_PALETTE;
export const CONTENT_BREAKDOWN_COLORS = [
  "#2f66ed",
  "#31d0bd",
  "#a855f7",
  "#5b9cf2",
  "#18bf74",
  "#f59e0b",
  "#8b5cf6",
  "#14b8a6",
] as const;
export const POSTS_PER_PAGE = 5;
export const CONTENT_TYPE_BAR_PAGE_SIZE = 5;

// Title is always sourced from the backend; this only normalizes casing
// (capitalizes the first letter of each word) so styling never diverges
// from whatever name the backend returns.
export const formatKpiTitle = (title: string, t: TFunction) =>
  formatOrganicContentKpiTitle(title, t, toTitleCase);

export const wrapLocalizedPostTypeLabel = (
  value: string,
  t: TFunction,
  wordsPerLine = 1,
  maxLines = 4
) => {
  const words = formatOrganicPostTypeLabel(value, t).split(/\s+/).filter(Boolean);
  const lines: string[] = [];

  for (let index = 0; index < words.length; index += wordsPerLine) {
    if (lines.length === maxLines - 1) {
      lines.push(words.slice(index).join(" "));
      break;
    }
    lines.push(words.slice(index, index + wordsPerLine).join(" "));
  }

  return lines;
};

export function ContentTypeAxisTick(props: SVGProps<SVGTextElement> & {
  x?: number;
  y?: number;
  payload?: { value: string };
}) {
  const { t } = useTranslation();
  const { x = 0, y = 0, payload } = props;
  const lines = wrapLocalizedPostTypeLabel(payload?.value ?? "", t);

  return (
    <g transform={`translate(${x},${y + 12})`}>
      <text
        x={0}
        y={0}
        dy={0}
        textAnchor="middle"
        fill="#334155"
        fontSize={12}
      >
        {lines.map((line, index) => (
          <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 0 : 13}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

export const formatTrendMetricLabel = (value: string, t: TFunction) => {
  const trimmed = value.trim();
  const normalized = trimmed.toLowerCase().replace(/[_\s]+/g, " ");
  if (normalized === "organic reach" || trimmed === "الوصول العضوي") {
    return t("organicMedia.instagram.reach");
  }
  // The "Posts Created" trend series is surfaced as "Viewers" in the chart.
  if (
    normalized === "posts created" ||
    trimmed === "المنشورات التي تم إنشاؤها" ||
    trimmed === "المشاركات التي تم إنشاؤها"
  ) {
    return t("organicMedia.instagram.viewers");
  }

  return formatKpiTitle(value, t);
};

export const INSTAGRAM_REACTION_KEYS = ["likes"];

export const formatInstagramEngagementLabel = (metricKey: string, t: TFunction) => {
  if (metricKey === "reactions") {
    return t("organicMedia.instagram.reactions");
  }
  // Falls back to the raw backend key (title-cased) when no translation exists,
  // so a renamed/new metric key from the backend still shows a sensible label
  // instead of a raw i18n key or a stale hardcoded name.
  const translated = t(`organicMedia.instagram.engagementMetrics.${metricKey}`, {
    defaultValue: "",
  });
  return translated || toTitleCase(metricKey);
};

export const parseDateValueToMs = (value: string, includeEndOfDay = false) => {
  const raw = String(value ?? "").trim();
  if (!raw) {return Number.NaN;}

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]) - 1;
    const day = Number(isoMatch[3]);
    const ms = Date.UTC(year, month, day);
    return includeEndOfDay ? ms + 24 * 60 * 60 * 1000 - 1 : ms;
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const month = Number(usMatch[1]) - 1;
    const day = Number(usMatch[2]);
    const year = Number(usMatch[3]);
    const ms = Date.UTC(year, month, day);
    return includeEndOfDay ? ms + 24 * 60 * 60 * 1000 - 1 : ms;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? Number.NaN : parsed.getTime();
};

export const getMonthBucket = (value: string) => {
  const raw = String(value ?? "").trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}`;
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    return `${usMatch[3]}-${String(Number(usMatch[1])).padStart(2, "0")}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
};

export const formatTrendDateByGranularity = (
  value: string | number,
  granularity: TrendGranularity,
  language?: string
) => {
  const raw = String(value ?? "");
  if (!raw) {return "";}

  const localizeDigits = (text: string) =>
    isArabicLanguage(language) ? toArabicIndicDigitsInString(text) : text;

  if (granularity === "monthly") {
    const monthMatch = raw.match(/^(\d{4})-(\d{2})$/);
    if (monthMatch) {
      return localizeDigits(`${monthMatch[2]}-${monthMatch[1]}`);
    }
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return localizeDigits(`${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`);
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const d = String(usMatch[2]).padStart(2, "0");
    const m = String(usMatch[1]).padStart(2, "0");
    return localizeDigits(`${d}-${m}-${usMatch[3]}`);
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {return localizeDigits(raw);}
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  return localizeDigits(`${dd}-${mm}-${parsed.getFullYear()}`);
};


export type EngagementDetailRow = {
  metricKey: string;
  value: number;
  /** Pre-localized sibling of `value`; absent when the backend hasn't sent it yet. */
  valueFormattedValue?: string;
  /** Omitted when the API does not return period-over-period change. */
  percentageChange?: number;
  /** Pre-localized sibling of `percentageChange`; mirrors its nullability. */
  percentageChangeFormattedValue?: string | null;
  comparisonLabel: string;
  trend: TrendPoint[];
};

export type EngagementCardRow = EngagementDetailRow & {
  details?: EngagementDetailRow[];
};

export function DonutTooltip({ active, payload }: TooltipProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const p = payload[0];
  // Recharts populates `percent` (0–1) on pie tooltip entries at runtime, but it
  // isn't declared on the Payload type — read it defensively.
  const percent = (p as { percent?: number }).percent;
  // Recharts always attaches the original datum (from `chartPostsByType`) under
  // `payload`, so the backend's pre-localized sibling is available here too.
  const valueFormattedValue = (p.payload as { valueFormattedValue?: string } | undefined)
    ?.valueFormattedValue;
  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.postType")}</span>
          <strong className="organic-tooltip__meta-value">
            {formatOrganicPostTypeLabel(String(p.name), t)}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.totalPosts")}</span>
          <strong className="organic-tooltip__meta-value">
            {valueFormattedValue ?? formatNumber(Number(p.value), {}, i18n.language)}
            {typeof percent === "number"
              ? withBidiIsolate(
                  ` (${formatNumber(percent * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%)`,
                  i18n.language
                )
              : ""}
          </strong>
        </div>
      </div>
    </div>
  );
}

export function HorizontalBarTooltip({
  active,
  payload,
  metricKey,
}: TooltipProps<number, string> & { metricKey: MetricKey }) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload as
    | { type: string; valueFormattedValue?: string }
    | undefined;
  const value = Number(payload[0]?.value ?? 0);
  const isPercentMetric = PERCENT_METRICS.includes(metricKey as MetricKey);
  const formattedValue =
    row?.valueFormattedValue ??
    (isPercentMetric
      ? withBidiIsolate(
          `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}%`,
          i18n.language
        )
      : formatNumber(value, {}, i18n.language));

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.postType")}</span>
          <strong className="organic-tooltip__meta-value">
            {row?.type ? formatOrganicPostTypeLabel(row.type, t) : "-"}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.metricValue")}</span>
          <strong className="organic-tooltip__meta-value">
            {formattedValue}
          </strong>
        </div>
      </div>
    </div>
  );
}

export function OverTimeTooltip({ active, label, payload }: TooltipProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const labelText = formatTrendDateByGranularity(
    String(label ?? ""),
    /^\d{4}-\d{2}$/.test(String(label ?? "")) ? "monthly" : "daily",
    i18n.language
  );
  const nonZeroRows = payload.filter(entry => Number(entry?.value ?? 0) > 0);
  const rowsToRender = nonZeroRows.length > 0 ? nonZeroRows : payload.slice(0, 1);

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.date")}</span>
          <strong className="organic-tooltip__meta-value">{labelText}</strong>
        </div>
        {rowsToRender.map(entry => (
          <div className="organic-tooltip__meta-line" key={String(entry.name)}>
            <span className="organic-tooltip__meta-label">
              {formatTrendMetricLabel(String(entry.name), t)}:
            </span>
            <strong className="organic-tooltip__meta-value">
              {formatNumber(Number(entry.value ?? 0), {}, i18n.language)}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}

// Trend lines mixing a 0–1-ish percentage metric (Engagement Rate) with a
// raw-count metric (Impressions/Reach/Viewers) need separate Y axes — sharing
// one linear axis squishes the percentage line flat against zero.

export const isPercentTrendCard = (card: { metricKey: string }) => {
  const normalized = card.metricKey;
  return normalized.includes("engagement") || normalized.includes("ctr");
};

export const getTrendState = (pct: number): "positive" | "negative" | "neutral" => {
  if (pct === 0) {return "neutral";}
  return pct > 0 ? "positive" : "negative";
};

export function formatBarMetricTick(metricKey: MetricKey, value: number | string, language: string) {
  const numericValue = Number(value ?? 0);
  if (PERCENT_METRICS.includes(metricKey)) {
    return withBidiIsolate(
      `${formatNumber(numericValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, language)}%`,
      language
    );
  }
  return formatNumber(numericValue, {}, language);
}

// Looks up the pre-localized sibling for whichever metric is currently
// selected on a `postTypeMetrics` row.
export function getPostTypeMetricFormattedValue(
  row: {
    engagementRateFormattedValue?: string;
    organicReachFormattedValue?: string;
    impressionsFormattedValue?: string;
  },
  metricKey: MetricKey
): string | undefined {
  switch (metricKey) {
    case "engagementRate":
      return row.engagementRateFormattedValue;
    case "organicReach":
      return row.organicReachFormattedValue;
    case "impressions":
      return row.impressionsFormattedValue;
    default:
      return undefined;
  }
}

export function getPercentAxisConfig(values: number[]): PercentAxisConfig {
  const maxValue = Math.max(0, ...values.map(value => Number(value ?? 0)));
  const step = maxValue <= 2 ? 0.5 : maxValue <= 5 ? 1 : 2;
  const upperBound = Math.max(step, Math.ceil(maxValue / step) * step);
  const tickCount = Math.max(2, Math.round(upperBound / step));
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) =>
    Number((index * step).toFixed(2))
  );

  return {
    domain: [0, upperBound] as [number, number],
    ticks,
  };
}
